import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, BellOff, CheckCheck } from 'lucide-react';
import { Skeleton } from '@/components/ui/Feedback';
import { cn } from '@/lib/cn';
import { NotificationItem } from './NotificationItem';
import { useMarkAllRead, useMarkRead, useMarkUnread, useNotifications, useRemoveNotification, type AppNotification } from './queries';

/**
 * The bell: unread count on the badge, the latest notifications in a panel,
 * All / Unread, mark all as read, and each one opening what it is about.
 * Checks for new ones every minute and whenever the tab regains focus, and
 * puts the unread count in the browser tab title.
 */
export function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  /*
    Opens on what is waiting for you rather than on everything that happened —
    the client asked the bell to carry what is pending or essential for that
    person, and a feed where a daily summary sits beside an expiring ticket
    buries the ticket.
  */
  const [filter, setFilter] = useState<'action' | 'unread' | 'all'>('action');
  const panelRef = useRef<HTMLDivElement>(null);

  // The badge is always polled; the panel's list loads when it opens.
  const summary = useNotifications({ perPage: 1, poll: true });
  const list = useNotifications({ filter, perPage: 15 });
  const markRead = useMarkRead();
  const markUnread = useMarkUnread();
  const markAll = useMarkAllRead();
  const remove = useRemoveNotification();

  const unread = summary.data?.meta.unread ?? 0;
  const needsYou = summary.data?.meta.needs_you ?? 0;
  const items = list.data?.data ?? [];

  useEffect(() => {
    const base = document.title.replace(/^\(\d+\+?\)\s*/, '');
    document.title = unread > 0 ? `(${unread > 99 ? '99+' : unread}) ${base}` : base;
  }, [unread]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: PointerEvent) => !panelRef.current?.contains(event.target as Node) && setOpen(false);
    const onKeyDown = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const openItem = (notification: AppNotification) => {
    if (!notification.read) markRead.mutate(notification.id);
    setOpen(false);
    if (notification.url) navigate(notification.url);
  };

  return (
    <div ref={panelRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => {
          setOpen((value) => !value);
          if (!open) list.refetch();
        }}
        aria-label={
          needsYou > 0
            ? `Notifications, ${needsYou} waiting on you`
            : unread > 0
              ? `Notifications, ${unread} unread`
              : 'Notifications'
        }
        aria-expanded={open}
        aria-haspopup="dialog"
        className={cn(
          'relative flex h-10 w-10 items-center justify-center rounded-full transition-colors',
          open ? 'bg-pale-blue text-body' : 'bg-cloud text-muted hover:bg-pale-blue hover:text-body',
        )}
      >
        <Bell aria-hidden className={cn('h-[18px] w-[18px]', needsYou > 0 && 'text-ink')} />

        {/*
          Something waiting on you rings quietly: a soft halo behind the badge,
          drawn once rather than animating forever, because a bell that pulses
          all day is a bell nobody looks at.
        */}
        {unread > 0 && (
          <>
            {needsYou > 0 && (
              <span aria-hidden className="absolute -right-0.5 -top-0.5 h-[18px] w-[18px] animate-ping rounded-full bg-danger/40" />
            )}
            <span
              className={cn(
                'absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-2xs font-bold leading-none text-white ring-2 ring-white',
                needsYou > 0 ? 'bg-danger' : 'bg-navy',
              )}
            >
              {unread > 99 ? '99+' : unread}
            </span>
          </>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Notifications"
          className="fixed inset-x-3 top-[calc(theme(spacing.topbar)+4px)] z-50 flex max-h-[min(640px,calc(100dvh-theme(spacing.topbar)-16px))] animate-fade-in flex-col overflow-hidden rounded-xl border border-line bg-white shadow-flyout sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[400px]"
        >
          <div className="flex items-center justify-between gap-3 border-b border-line px-4 pb-2 pt-3">
            <div>
              <p className="text-base font-semibold text-ink">Notifications</p>
              <div className="mt-2 flex gap-1" role="tablist">
                {(
                  [
                    ['action', 'Needs you', needsYou],
                    ['unread', 'Unread', unread],
                    ['all', 'All', 0],
                  ] as const
                ).map(([value, label, count]) => (
                  <button
                    key={value}
                    type="button"
                    role="tab"
                    aria-selected={filter === value}
                    onClick={() => setFilter(value)}
                    className={cn(
                      'rounded-full px-3 py-1 text-helper font-semibold transition-colors',
                      filter === value ? 'bg-accent-50 text-accent-700' : 'text-muted hover:bg-cloud',
                    )}
                  >
                    {label}
                    {count > 0 ? ` (${count})` : ''}
                  </button>
                ))}
              </div>
            </div>
            <button
              type="button"
              disabled={unread === 0 || markAll.isPending}
              onClick={() => markAll.mutate()}
              className="flex items-center gap-1.5 self-start rounded-md px-2 py-1 text-helper font-semibold text-digital hover:bg-cloud disabled:text-muted/60 disabled:hover:bg-transparent"
            >
              <CheckCheck aria-hidden className="h-4 w-4" />
              Mark all as read
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto scrollbar-slim">
            {list.isPending ? (
              <div className="space-y-3 p-4">
                {Array.from({ length: 4 }).map((_, index) => <Skeleton key={index} className="h-14 rounded-lg" />)}
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center px-6 py-12 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-cloud text-muted">
                  <BellOff aria-hidden className="h-5 w-5" />
                </span>
                <p className="mt-3 text-sm font-semibold text-ink">
                  {filter === 'action'
                    ? 'Nothing is waiting on you'
                    : filter === 'unread'
                      ? 'You are all caught up'
                      : 'No notifications yet'}
                </p>
                <p className="mt-1 text-helper text-muted">
                  {filter === 'action'
                    ? 'KPIs to fill in, certifications to review and site flags appear here the moment they need you.'
                    : 'Certifications to review, site flags, bills to approve and deadlines will show up here.'}
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-line">
                {items.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onOpen={() => openItem(notification)}
                    onToggleRead={() => (notification.read ? markUnread : markRead).mutate(notification.id)}
                    onRemove={() => remove.mutate(notification.id)}
                  />
                ))}
              </ul>
            )}
          </div>

          <Link to="/notifications" onClick={() => setOpen(false)} className="border-t border-line py-2.5 text-center text-sm font-semibold text-digital hover:bg-cloud">
            View all notifications
          </Link>
        </div>
      )}
    </div>
  );
}
